import { PropertyDetails, PropertyEquity, UserData } from "utils/types";
import { stateSpecificAppealWord, isEnterprise } from "utils/account";
import { getInsuranceStatus } from ".";
import { usePropertyContext } from "components/pages/Account/Property/context";
import React from "react";
import { usePropertyEquityContext } from "components/pages/Account/Property/PropertyEquityContext";
import { getMostRecentAppeal } from "utils/getMostRecentAppeal";
import { useAuthContext } from "context/AuthContext";
import { useExemptionsContext } from "components/pages/Account/Property/ExemptionsContext";
import moment from "moment";
import { RefinanceMonitoring } from "components/pages/Account/Property/RefinanceMonitoring";
import { useConciergeContext } from "components/pages/Account/Property/Concierge/ConciergePage";

export namespace Services {
	export const statuses = [
		"Loading",
		"Unavailable",
		"Inactive",
		"Warning",
		"Active",
		"ActionsAvailable",
	] as const;
	export type Status = (typeof statuses)[number];

	export const ids = [
		"appeal",
		"exemption",
		"insurance",
		"bill_reduction_internet",
		"bill_reduction_electric",
		"bill_reduction_phone",
		"mortgage",
		"expenses",
		"bill_reduction_cable_or_satellite",
		"bill_reduction_security",
		"bill_reduction_home_phone",
		"bill_reduction_other",
		"refinance_monitoring",
		"concierge",
	] as const;
	export type Id = (typeof ids)[number];

	export type WaysToSaveIds = Exclude<
		Id,
		| "expenses"
		| "bill_reduction_cable_or_satellite"
		| "bill_reduction_security"
		| "bill_reduction_home_phone"
		| "bill_reduction_other"
		| "refinance_monitoring"
		| "concierge"
	>;
	export const isWaysToSaveId = (id: Id): id is WaysToSaveIds => {
		return ![
			"expenses",
			"bill_reduction_cable_or_satellite",
			"bill_reduction_security",
			"bill_reduction_home_phone",
			"bill_reduction_other",
			"refinance_monitoring",
		].includes(id);
	};

	export type BillReductionIds = Exclude<
		Id,
		| "expenses"
		| "appeal"
		| "exemption"
		| "insurance"
		| "mortgage"
		| "expenses"
		| "refinance_monitoring"
		| "concierge"
	>;
	export const isBillReductionId = (id: Id): id is BillReductionIds => {
		return ![
			"expenses",
			"appeal",
			"exemption",
			"insurance",
			"mortgage",
			"expenses",
		].includes(id);
	};

	export type Context = {
		refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		propertyDetails: PropertyDetails;
		equityDetails: PropertyEquity.Response;
		allProperties: PropertyDetails[];
		userData: UserData;
		exemptionsContext: ReturnType<typeof useExemptionsContext>;
		conciergeContext: ReturnType<typeof useConciergeContext>;
	};

	type AbstractConstructorParameters = {
		propertyDetails: PropertyDetails | undefined;
		equityDetails: PropertyEquity.Response | undefined;
		allProperties: PropertyDetails[] | undefined;
		userData: UserData | undefined;
		exemptionsContext: ReturnType<typeof useExemptionsContext>;
		refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		conciergeContext: ReturnType<typeof useConciergeContext>;
	};
	export abstract class AbstractService {
		private propertyDetails: PropertyDetails | undefined;
		private equityDetails: PropertyEquity.Response | undefined;
		private allProperties: PropertyDetails[] | undefined;
		private userData: UserData | undefined;
		private exemptionsContext: ReturnType<typeof useExemptionsContext>;
		private refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		private conciergeContext: ReturnType<typeof useConciergeContext>;
		constructor({
			refinanceMonitoringContext,
			propertyDetails,
			equityDetails,
			allProperties,
			userData,
			exemptionsContext,
			conciergeContext,
		}: AbstractConstructorParameters) {
			this.refinanceMonitoringContext = refinanceMonitoringContext;
			this.propertyDetails = propertyDetails;
			this.equityDetails = equityDetails;
			this.allProperties = allProperties;
			this.userData = userData;
			this.exemptionsContext = exemptionsContext;
			this.conciergeContext = conciergeContext;
		}

		public abstract get id(): Id;
		public abstract get name(): string;
		public abstract get categories(): string | string[]; //Added this new to make it more applicable to both WaysToSave and BillReductionWaysToSave --- !! KAT RENAME TO MAYBE "location" or something visibility
		protected get state(): string {
			return this.propertyDetails?.state ?? "";
		}

		protected get context(): Context | undefined {
			if (
				!this.refinanceMonitoringContext ||
				!this.propertyDetails ||
				!this.equityDetails ||
				!this.allProperties?.length ||
				!this.userData ||
				!this.exemptionsContext
			)
				return undefined;
			return {
				refinanceMonitoringContext: this.refinanceMonitoringContext,
				propertyDetails: this.propertyDetails,
				equityDetails: this.equityDetails,
				allProperties: this.allProperties,
				userData: this.userData,
				exemptionsContext: this.exemptionsContext,
				conciergeContext: this.conciergeContext,
			};
		}

		protected abstract isAvailable(context: Context): boolean;
		protected abstract getStatus(context: Context): Status;

		public resolveStatus = (): Status => {
			if (!this.context) return "Loading";
			if (!this.isAvailable(this.context)) return "Unavailable";
			return this.getStatus(this.context);
		};
	}

	class AppealService extends AbstractService {
		get id() {
			return "appeal" as const;
		}

		get name() {
			return `Tax ${stateSpecificAppealWord(this.state, {
				capitalize: true,
			})}`;
		}

		isAvailable = ({ propertyDetails }: Context) => {
			return propertyDetails?.available_services?.includes("appeal");
		};

		getStatus = ({ propertyDetails }: Context) => {
			const mostRecentAppeal = getMostRecentAppeal(propertyDetails);

			if (
				mostRecentAppeal?.needs_attention_reason.length &&
				!mostRecentAppeal?.needs_attention_reason.every(
					reason => reason.documentsUnderReview
				) &&
				!mostRecentAppeal?.needs_attention_reason.every(
					reason => reason.documentsFailedReview
				)
			) {
				return "Warning";
			}

			if (
				!!propertyDetails?.auto_appeal ||
				(mostRecentAppeal?.tax_year === new Date().getFullYear() &&
					mostRecentAppeal.stage !== "Archive")
			) {
				return "Active";
			}

			return "Inactive";
		};

		get categories() {
			return ["WaysToSave"];
		}
	}

	class ExemptionService extends AbstractService {
		get id() {
			return "exemption" as const;
		}

		get name() {
			return "Exemption";
		}

		get categories() {
			return ["WaysToSave"];
		}

		isAvailable = ({
			propertyDetails,
			allProperties,
			userData,
			exemptionsContext,
		}: Context) => {
			if (
				propertyDetails?.auth_company_type === "LLC" &&
				propertyDetails?.state === "TX"
			) {
				return false;
			}
			if (
				isEnterprise(userData) &&
				propertyDetails.available_services.includes("exemption")
			) {
				return true;
			}

			if (
				exemptionsContext.primaryResidenceValue === false &&
				exemptionsContext?.status !== "Started"
			) {
				return false;
			} else if (
				!propertyDetails.exemption &&
				propertyDetails.q_primary_residence === 0 &&
				exemptionsContext?.status !== "Started"
			) {
				return false;
			}

			const allPropertiesWithActiveExemptions = allProperties
				.filter(property => {
					return (
						property.exemption_status &&
						!["Archive", "Started"].includes(property.exemption_status)
					);
				})
				.map(property => property.id);

			// Deactivate exemptions service for customers who have indicated that another property is their primary residence
			if (
				allPropertiesWithActiveExemptions?.length &&
				!allPropertiesWithActiveExemptions?.includes(propertyDetails.id) &&
				exemptionsContext?.status !== "Started"
			) {
				return false;
			}

			if (
				propertyDetails?.exemption_status === "Archive" &&
				propertyDetails?.exemption?.archive_reason ===
					"Not Primary Residence" &&
				exemptionsContext?.status !== "Started"
			) {
				return false;
			}

			return propertyDetails.available_services.includes("exemption");
		};

		getStatus = ({ propertyDetails, exemptionsContext }: Context) => {
			const { exemption_status, is_homestead, exemption } = propertyDetails;

			if (
				["Signed", "Verified", "On Hold", "Filed"].includes(
					exemption_status ?? ""
				)
			) {
				if (!!exemptionsContext.showDriversLicenseNARs) {
					return "Warning";
				}

				if (
					exemption?.needs_attention_reason?.split(",").filter(v => !!v)?.length
				) {
					return "Warning";
				}
			}

			if (
				!propertyDetails.is_homestead &&
				!!propertyDetails.homestead_fell_off_at &&
				moment(propertyDetails.homestead_fell_off_at).isBefore(
					moment().subtract(3, "day")
				)
			) {
				return "Warning";
			}

			if (
				is_homestead ||
				[
					"Signed",
					"Verified",
					"On Hold",
					"Ready to File",
					"Filed",
					"Active",
				].includes(exemption_status ?? "")
			) {
				return "Active";
			}

			if (exemption_status === "Archive") {
				if (
					exemption?.archive_reason === "Already Has Homestead" ||
					exemption?.archive_reason === "Research Survey Completed"
				) {
					return "Active";
				}
				return "Unavailable";
			}

			return "Inactive";
		};
	}

	class InsuranceService extends AbstractService {
		get id() {
			return "insurance" as const;
		}

		get name() {
			return "Insurance";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			const insuranceStatus = getInsuranceStatus(propertyDetails);

			if (insuranceStatus === "ACTIVATED") {
				return "Active";
			}

			if (insuranceStatus === "QUOTES_READY") {
				return "ActionsAvailable";
			}

			return "Inactive";
		};

		get categories() {
			return ["WaysToSave"];
		}
	}

	class InternetService extends AbstractService {
		get id() {
			return "bill_reduction_internet" as const;
		}

		get name() {
			return "Internet Bill";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Internet"
			)
				? "Active"
				: "Inactive";
		};
		get categories() {
			return ["WaysToSave", "BillReductions"];
		}
	}

	class ElectricService extends AbstractService {
		get id() {
			return "bill_reduction_electric" as const;
		}

		get name() {
			return "Electric Bill";
		}

		isAvailable = ({ userData, propertyDetails }: Context) => {
			return !isEnterprise(userData) && propertyDetails.state == "TX";
		};

		getStatus = ({ propertyDetails }: Context) => {
			if (
				propertyDetails?.light_enrollment &&
				propertyDetails.light_enrollment?.enrolled == false &&
				propertyDetails.light_enrollment?.status == "completed" &&
				propertyDetails.light_enrollment?.projected_bill_data
			) {
				return "ActionsAvailable";
			} else if (
				propertyDetails?.light_enrollment &&
				propertyDetails.light_enrollment?.enrolled == true
			) {
				return "Active";
			} else {
				return propertyDetails.bill_reduction?.find(
					({ category }) => category === "Electric"
				)
					? "Active"
					: "Inactive";
			}
		};
		get categories() {
			return ["WaysToSave", "BillReductions"];
		}
	}

	class PhoneService extends AbstractService {
		get id() {
			return "bill_reduction_phone" as const;
		}

		get name() {
			return "Reduce Phone Bill";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Cell Phone"
			)
				? "Active"
				: "Inactive";
		};

		get categories() {
			return ["BillReductions"];
		}
	}

	class CableOrSatelliteService extends AbstractService {
		get id() {
			return "bill_reduction_cable_or_satellite" as const;
		}

		get name() {
			return "Cable / Satellite TV";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Cable / Satellite TV"
			)
				? "Active"
				: "Inactive";
		};
		get categories() {
			return ["BillReductions"];
		}
	}

	class SecurityService extends AbstractService {
		get id() {
			return "bill_reduction_security" as const;
		}

		get name() {
			return "Security System";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Security System"
			)
				? "Active"
				: "Inactive";
		};
		get categories() {
			return ["BillReductions"];
		}
	}

	class HomePhoneService extends AbstractService {
		get id() {
			return "bill_reduction_home_phone" as const;
		}

		get name() {
			return "Home Phone";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Home Phone"
			)
				? "Active"
				: "Inactive";
		};
		get categories() {
			return ["BillReductions"];
		}
	}

	class OtherBillsService extends AbstractService {
		get id() {
			return "bill_reduction_other" as const;
		}

		get name() {
			return "Other Bills";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.bill_reduction?.find(
				({ category }) => category === "Other"
			)
				? "Active"
				: "Inactive";
		};
		get categories() {
			return ["BillReductions"];
		}
	}

	class MortgageService extends AbstractService {
		get id() {
			return "mortgage" as const;
		}

		get name() {
			return "Mortgage & Equity";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.mortgage_monitoring_enabled
				? "Active"
				: "Inactive";
		};

		get categories() {
			return ["WaysToSave"];
		}
	}

	class RefinanceMonitoringService extends AbstractService {
		get id() {
			return "refinance_monitoring" as const;
		}

		get name() {
			return "Refinance Monitoring";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ refinanceMonitoringContext }: Context) => {
			return refinanceMonitoringContext?.survey_values?.refinance_monitoring
				?.completed_survey
				? "Active"
				: "Inactive";
		};

		get categories() {
			return [];
		}
	}

	class ConciergeService extends AbstractService {
		get id() {
			return "concierge" as const;
		}

		get name() {
			return "Concierge";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ conciergeContext }: Context) => {
			return conciergeContext?.conciergePaid ? "Active" : "Inactive";
		};

		get categories() {
			return [];
		}
	}

	//We are hiding this service for now
	class ExpensesService extends AbstractService {
		get id() {
			return "expenses" as const;
		}

		get name() {
			return "Expenses";
		}

		isAvailable = ({ userData }: Context) => {
			return !isEnterprise(userData);
		};

		getStatus = ({ propertyDetails }: Context) => {
			return propertyDetails.mortgage_monitoring_enabled
				? "Active"
				: "Inactive";
		};

		get categories() {
			return [];
		}
	}

	export const getMap = (args: {
		propertyDetails: PropertyDetails | undefined;
		equityDetails: PropertyEquity.Response | undefined;
		allProperties: PropertyDetails[] | undefined;
		userData: UserData | undefined;
		exemptionsContext: ReturnType<typeof useExemptionsContext>;
		refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		conciergeContext: ReturnType<typeof useConciergeContext>;
	}) => {
		return {
			appeal: new AppealService(args),
			exemption: new ExemptionService(args),
			mortgage: new MortgageService(args),
			insurance: new InsuranceService(args),
			bill_reduction_internet: new InternetService(args),
			bill_reduction_electric: new ElectricService(args),
			bill_reduction_phone: new PhoneService(args),
			expenses: new ExpensesService(args),
			bill_reduction_cable_or_satellite: new CableOrSatelliteService(args),
			bill_reduction_security: new SecurityService(args),
			bill_reduction_home_phone: new HomePhoneService(args),
			bill_reduction_other: new OtherBillsService(args),
			refinance_monitoring: new RefinanceMonitoringService(args),
			concierge: new ConciergeService(args),
		};
	};

	export const getAll = (args: {
		propertyDetails: PropertyDetails | undefined;
		equityDetails: PropertyEquity.Response | undefined;
		allProperties: PropertyDetails[] | undefined;
		userData: UserData | undefined;
		exemptionsContext: ReturnType<typeof useExemptionsContext>;
		refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		conciergeContext: ReturnType<typeof useConciergeContext>;
	}) => {
		return Object.values(getMap(args));
	};

	const filterByWaysToSave = (services: AbstractService[]) => {
		return services.filter(service =>
			service.categories.includes("WaysToSave")
		) as AbstractService[];
	};

	const filterByBillReductions = (services: AbstractService[]) => {
		return services.filter(service =>
			service.categories.includes("BillReductions")
		) as AbstractService[];
	};

	const filterByVisibility = (
		services: AbstractService[],
		availableServices: Id[]
	) => {
		return services.filter(service => availableServices.includes(service.id));
	};

	export const filterByAvailability = (services: AbstractService[]) => {
		return services.filter(
			service => service.resolveStatus() !== "Unavailable"
		);
	};

	export const sortServices = (services: AbstractService[]) => {
		const order: Id[] = [
			"appeal",
			"exemption",
			"insurance",
			"bill_reduction_internet",
			"bill_reduction_electric",
			"bill_reduction_phone",
			"bill_reduction_cable_or_satellite",
			"bill_reduction_security",
			"bill_reduction_home_phone",
			"bill_reduction_other",
			"mortgage",
			"concierge",
		];
		// maintain order of services, if service is unavailable service.resolveStatus() === "Unavailable" it will be at the end
		return [...services].sort((a, b) => {
			const statusA = a.resolveStatus() === "Unavailable";
			const statusB = b.resolveStatus() === "Unavailable";
			if (statusA && !statusB) return 1;
			if (!statusA && statusB) return -1;
			return order.indexOf(a.id) - order.indexOf(b.id);
		});
	};

	export const getAllAvailable = (args: {
		propertyDetails: PropertyDetails | undefined;
		equityDetails: PropertyEquity.Response;
		allProperties: PropertyDetails[] | undefined;
		userData: UserData | undefined;
		exemptionsContext: ReturnType<typeof useExemptionsContext>;
		refinanceMonitoringContext: ReturnType<
			typeof RefinanceMonitoring.useContext
		>;
		conciergeContext: ReturnType<typeof useConciergeContext>;
	}) => {
		return filterByAvailability(getAll(args));
	};

	const useServiceContext = () => {
		const { details: propertyDetails } = usePropertyContext();
		const equityDetails = usePropertyEquityContext();
		const { properties: allProperties } = useAuthContext();
		const { userData } = useAuthContext();
		const exemptionsContext = useExemptionsContext();
		const refinanceMonitoringContext = RefinanceMonitoring.useContext();
		const conciergeContext = useConciergeContext();
		return {
			propertyDetails,
			equityDetails,
			allProperties,
			userData,
			exemptionsContext,
			refinanceMonitoringContext,
			conciergeContext,
		};
	};

	export const useServices = () => {
		const context = useServiceContext();
		const { details } = usePropertyContext();

		const allServices = React.useMemo(
			() =>
				filterByVisibility(getAll(context), details?.available_services ?? []),
			[context, details]
		);

		const availableServices = React.useMemo(
			() => filterByAvailability(allServices),
			[allServices]
		);
		const waysToSaveServices = React.useMemo(
			() => sortServices(filterByWaysToSave(allServices)),
			[allServices]
		);
		const availableWaysToSaveServices = React.useMemo(
			() => sortServices(filterByWaysToSave(availableServices)),
			[availableServices]
		);

		const billReductionServices = React.useMemo(
			() => sortServices(filterByBillReductions(allServices)),
			[allServices]
		);
		const availableBillReductionServices = React.useMemo(
			() => sortServices(filterByBillReductions(availableServices)),
			[availableServices]
		);
		return {
			allServices,
			waysToSaveServices,
			availableServices,
			availableWaysToSaveServices,
			billReductionServices,
			availableBillReductionServices,
		};
	};

	export const useAvailableServiceIds = () => {
		const { availableServices } = useServices();
		return availableServices.map(service => service.id);
	};

	export const useServiceStatus = (serviceId: Id): Status => {
		const context = useServiceContext();
		return getMap(context)[serviceId].resolveStatus();
	};
}
